/**
 * Route Detail — view a single route.
 * Stub for Phase 4. Full implementation in Phase 6.
 */

import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import '@material/web/progress/linear-progress.js'
import { db } from '../../database/schema.js'
import { router } from '../../utils/router.js'
import { routeStorage } from '../../storage/route-storage.js'
import { syncChildrenOnNavigate } from '../../storage/sync-manager.js'
import type { Route } from '../../schemas/route.js'
import type { Plan } from '../../schemas/plan.js'
import type { Activity } from '../../schemas/activity.js'

@customElement('route-detail')
export class RouteDetail extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      background: var(--cairn-background-color, #f8fafc);
    }

    .sync-bar {
      --md-linear-progress-active-indicator-color: var(--cairn-primary-color, #3b82f6);
      --md-linear-progress-track-color: transparent;
      --md-linear-progress-track-height: 2px;
      --md-linear-progress-active-indicator-height: 2px;
      width: 100%;
      flex-shrink: 0;
    }

    .container {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow: hidden;
    }

    .content-scroll {
      flex: 1;
      overflow: auto;
      transition: transform 0.15s ease-out;
    }

    .header {
      background: var(--cairn-surface-color, #ffffff);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      padding: var(--cairn-spacing-md, 16px);
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      flex-shrink: 0;
    }

    .back-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
    }

    .back-button:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
    }

    .title {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
    }

    .content {
      flex: 1;
      padding: var(--cairn-spacing-lg, 24px);
      max-width: 800px;
    }

    .meta {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
      margin-bottom: var(--cairn-spacing-sm, 8px);
    }

    .meta .material-icons {
      font-size: 16px;
    }

    .tags {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .tag {
      font-size: 12px;
      padding: 2px 8px;
      border-radius: 12px;
      background: var(--cairn-surface-hover-color, #f1f5f9);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .difficulty-badge {
      display: inline-flex;
      font-size: 12px;
      font-weight: 500;
      text-transform: uppercase;
      padding: 2px 8px;
      border-radius: 12px;
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .difficulty-badge.beginner {
      background: #dcfce7;
      color: #166534;
    }
    .difficulty-badge.intermediate {
      background: #dbeafe;
      color: #1e40af;
    }
    .difficulty-badge.advanced {
      background: #fef3c7;
      color: #92400e;
    }
    .difficulty-badge.expert {
      background: #fecaca;
      color: #991b1b;
    }

    .actions {
      display: flex;
      gap: var(--cairn-spacing-md, 16px);
      margin-top: var(--cairn-spacing-lg, 24px);
    }

    .related-section {
      margin-top: var(--cairn-spacing-lg, 24px);
      padding-top: var(--cairn-spacing-md, 16px);
      border-top: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .related-heading {
      font-size: var(--cairn-text-size-sm, 14px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-secondary-color, #64748b);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin: 0 0 var(--cairn-spacing-sm, 8px) 0;
    }

    .related-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .related-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: var(--cairn-text-size-sm, 14px);
      padding: 4px 10px;
      border-radius: 12px;
      background: var(--cairn-surface-hover-color, #f1f5f9);
      color: var(--cairn-text-color, #1e293b);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      cursor: pointer;
      transition: all var(--cairn-animation-duration, 0.2s);
    }

    .related-chip:hover {
      border-color: var(--cairn-primary-color, #3b82f6);
      color: var(--cairn-primary-color, #3b82f6);
    }

    .related-chip .material-icons {
      font-size: 14px;
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-weight: normal;
      font-style: normal;
      line-height: 1;
      display: inline-block;
    }

    @media (min-width: 769px) {
      .header {
        display: none;
      }
    }
  `

  @property({ type: String }) routeId?: string
  @state() private route: Route | null = null
  @state() private syncing = false
  @state() private relatedPlans: Plan[] = []
  @state() private relatedActivities: Activity[] = []

  override connectedCallback(): void {
    super.connectedCallback()
    if (this.routeId) {
      void this.loadRoute()
      this.syncing = true
      void syncChildrenOnNavigate('route', this.routeId)
        .then(() => this.loadRoute())
        .finally(() => {
          this.syncing = false
        })
    }
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('routeId') && this.routeId) void this.loadRoute()
  }

  private async loadRoute(): Promise<void> {
    if (!this.routeId) return
    this.route = (await db.routes.get(this.routeId)) ?? null
    await this.loadRelated()
  }

  private async loadRelated(): Promise<void> {
    if (!this.routeId) return
    const activityIds = await routeStorage.getActivityIdsFollowingRoute(this.routeId)
    const activities = await Promise.all(activityIds.map(id => db.activities.get(id)))
    this.relatedPlans = []
    this.relatedActivities = activities.filter((a): a is Activity => a !== undefined)
  }

  // --- Pull-to-refresh ---
  private pullStartY = 0
  private pulling = false
  private scrollerEl: HTMLElement | null = null

  private handleTouchStart(e: TouchEvent): void {
    const scroller = e.currentTarget as HTMLElement
    this.scrollerEl = scroller
    if (scroller.scrollTop <= 0) {
      this.pullStartY = e.touches[0].clientY
      this.pulling = true
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.pulling || !this.scrollerEl) return
    const dy = e.touches[0].clientY - this.pullStartY
    if (dy > 0) {
      const clamped = Math.min(dy * 0.4, 80)
      this.scrollerEl.style.transform = `translateY(${clamped}px)`
    } else {
      this.pulling = false
      this.scrollerEl.style.transform = ''
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (!this.pulling) return
    const dy = e.changedTouches[0].clientY - this.pullStartY
    this.pulling = false
    if (this.scrollerEl) this.scrollerEl.style.transform = ''
    if (dy > 60) {
      void this.refresh()
    }
  }

  private async refresh(): Promise<void> {
    if (!this.routeId) return
    this.syncing = true
    try {
      await syncChildrenOnNavigate('route', this.routeId)
      await this.loadRoute()
    } finally {
      this.syncing = false
    }
  }

  override render(): unknown {
    if (!this.route) {
      return html`<div class="container">
        <div class="content"><p>Loading route...</p></div>
      </div>`
    }

    const r = this.route
    return html`
      <div class="container">
        <div class="header">
          <button class="back-button" @click=${() => window.history.back()}>
            <span class="material-icons">arrow_back</span>
          </button>
          <h1 class="title">${r.name}</h1>
        </div>
        ${this.syncing
          ? html`<md-linear-progress indeterminate class="sync-bar"></md-linear-progress>`
          : nothing}
        <div
          class="content-scroll"
          @touchstart=${this.handleTouchStart}
          @touchmove=${this.handleTouchMove}
          @touchend=${this.handleTouchEnd}
        >
          <div class="content">
            ${r.difficulty
              ? html`<span class="difficulty-badge ${r.difficulty}">${r.difficulty}</span>`
              : ''}
            ${r.tags.length > 0
              ? html`<div class="tags">
                  ${r.tags.map(t => html`<span class="tag">${t}</span>`)}
                </div>`
              : ''}
            ${r.description ? html`<p>${r.description}</p>` : ''}

            <div class="actions">
              <md-text-button @click=${() => router.navigate(`/plans/new?routeId=${r.id}`)}>
                <span class="material-icons" slot="icon">map</span>
                Start a Plan
              </md-text-button>
            </div>

            ${this.relatedPlans.length > 0 || this.relatedActivities.length > 0
              ? html`
                  <div class="related-section">
                    <h2 class="related-heading">Related</h2>
                    ${this.relatedPlans.length > 0
                      ? html`
                          <div class="related-chips">
                            ${this.relatedPlans.map(
                              p => html`
                                <button
                                  class="related-chip"
                                  @click=${() => router.navigate(`/plans/${p.id}`)}
                                >
                                  <span class="material-icons">map</span>
                                  ${p.name}
                                </button>
                              `
                            )}
                          </div>
                        `
                      : ''}
                    ${this.relatedActivities.length > 0
                      ? html`
                          <div class="related-chips">
                            ${this.relatedActivities.map(
                              a => html`
                                <button
                                  class="related-chip"
                                  @click=${() => router.navigate(`/activities/${a.id}`)}
                                >
                                  <span class="material-icons">directions_walk</span>
                                  ${a.name}
                                </button>
                              `
                            )}
                          </div>
                        `
                      : ''}
                  </div>
                `
              : ''}
          </div>
        </div>
      </div>
    `
  }
}

import '@material/web/button/text-button.js'

declare global {
  interface HTMLElementTagNameMap {
    'route-detail': RouteDetail
  }
}
